/**
 * Which outgoing requests browser tracing may open a span for.
 *
 * The IBM Aspera desktop app is not a server of ours. `@ibm-aspera/sdk` reaches it over
 * loopback as JSON-RPC -- `POST http://127.0.0.1:33024/`, one fetch per call -- and
 * `get_all_transfers` answers with every transfer the app is holding, each carrying its own
 * transfer spec and path list, unpaginated. An upload polls that call twice a second
 * (`authorized-upload-handler.js`), so with `tracesSampleRate: 1.0` a user whose desktop app
 * has a long transfer history fills our performance data with multi-megabyte loopback spans,
 * and Sentry's large-payload detector raises them as issues against the 3x project
 * (PLATFORM-4201).
 *
 * The response size belongs to a third-party app running on the user's own machine: there is
 * nothing in it we can fix and nothing in it we learn from tracing, so no span is opened.
 */

// `localhost`, anything in 127.0.0.0/8, and IPv6 loopback in the bracketed form `URL` reports.
const LOOPBACK_HOSTNAME = /^(?:localhost|127\.\d+\.\d+\.\d+|\[::1\])$/i;

/**
 * Sentry hands these the URL exactly as it was passed to `fetch`/`open`, which may be relative.
 * Deliberately parsed without a base: a relative URL is a call to our own origin, which is what
 * tracing is for, and resolving it would make the answer depend on where the app is served from.
 *
 * @param {string} url Absolute; a relative or unparseable one counts as not loopback.
 * @returns {boolean}
 */
export const isLoopbackUrl = (url) => {
	try {
		return LOOPBACK_HOSTNAME.test(new URL(String(url)).hostname);
	}
	catch{
		return false;
	}
};

/**
 * `browserTracingIntegration`'s `shouldCreateSpanForRequest`.
 *
 * @param {string} url
 * @returns {boolean}
 */
export const shouldTraceRequest = (url) => !isLoopbackUrl(url);
